import React, { useEffect, useState, useRef } from 'react';
import EmployeeService, { EmployeeProfile } from '../services/EmployeeService';
import httpService from '../../../core/services/HttpService';
import WorkAreaService from '../../workareas/services/WorkAreaService';
import { FaSave, FaTimes } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { prepareProfilePatch } from '../utils/prepareProfilePatch';
import { normalizeGender } from '../services/profile_value_mapping';
import {
  GENDER_OPTIONS,
  LOHNSTKL_OPTIONS,
  RELIGION_OPTIONS,
  HEALTH_INS_TYPE_OPTIONS,
} from '../utils/constants';
import PaymentInstructionSection from './PaymentInstructionSection';
import ContractHistoryTimeline from './ContractHistoryTimeline';
import { QualificationPills } from './QualificationPills';
import {
  createCompensationPeriod,
  replaceCompensationPeriod,
  CompensationType,
} from '../services/CompensationService';
import { personalakteStyles as ps, colors } from '../styles/personalakteStyles';

interface EmployeeProfileTabProps {
  employeeId: number;
  initialProfile: any;
  onProfileChanged?: (profile: any) => void;
  refetchProfile: () => Promise<void>;
  refetchOverview?: () => Promise<void>;
  highlightField?: string | null;
  onHighlightDone?: () => void;
}

const EmployeeProfileTab: React.FC<EmployeeProfileTabProps> = ({
  employeeId,
  initialProfile,
  onProfileChanged,
  refetchProfile,
  refetchOverview,
  highlightField,
  onHighlightDone,
}) => {
  const [profile, setProfile] = useState<any>(initialProfile);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [workAreaOptions, setWorkAreaOptions] = useState<Array<{ id: number; name: string }>>([]);
  const initialProfileRef = useRef<any>(initialProfile);
  const pendingCompRef = useRef<{ type: CompensationType; amount: string; valid_from: string } | null>(null);
  const uniformRef = useRef<{ clothing_size?: string; clothing_cut?: string; has_received_uniform?: boolean | null }>({});

  const emailRef = useRef<HTMLDivElement>(null);
  const phoneRef = useRef<HTMLDivElement>(null);
  const birthDateRef = useRef<HTMLDivElement>(null);

  const fieldRefs: Record<string, React.RefObject<HTMLDivElement>> = {
    email: emailRef,
    phone: phoneRef,
    birth_date: birthDateRef,
  };

  useEffect(() => {
    if (highlightField && fieldRefs[highlightField]?.current) {
      const ref = fieldRefs[highlightField];
      setTimeout(() => {
        ref.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        ref.current?.classList.add('field-highlight');
        setTimeout(() => {
          ref.current?.classList.remove('field-highlight');
          onHighlightDone?.();
        }, 2000);
      }, 100);
    }
  }, [highlightField]);

  useEffect(() => {
    const validReligions = RELIGION_OPTIONS.map(o => o.value);

    const sanitized = initialProfile ? JSON.parse(JSON.stringify(initialProfile)) : initialProfile;
    if (sanitized?.person?.gender) {
      sanitized.person.gender = normalizeGender(sanitized.person.gender);
    }
    if (sanitized?.payroll?.religion && !validReligions.includes(sanitized.payroll.religion)) {
      sanitized.payroll.religion = null;
    }

    // Preserve working_pattern fields the server might not return
    if (sanitized?.working_pattern && !sanitized.working_pattern.regular_workdays) {
      const prevWd = initialProfileRef.current?.working_pattern?.regular_workdays;
      if (prevWd) {
        sanitized.working_pattern = { ...sanitized.working_pattern, regular_workdays: prevWd };
      }
    }
    // Merge cached uniform values (these live on Employee model, not in profile response)
    if (uniformRef.current.clothing_size) sanitized.clothing_size = uniformRef.current.clothing_size;
    if (uniformRef.current.clothing_cut) sanitized.clothing_cut = uniformRef.current.clothing_cut;
    if (uniformRef.current.has_received_uniform != null) sanitized.has_received_uniform = uniformRef.current.has_received_uniform;

    setProfile(sanitized);
    initialProfileRef.current = JSON.parse(JSON.stringify(sanitized));

    if (initialProfile?.job?.work_areas_detail) {
      setWorkAreaOptions(initialProfile.job.work_areas_detail);
    }

    // Fetch uniform fields from employee endpoint (not in profile response)
    let uniformAlive = true;
    httpService.get<any>(`/api/employees/${employeeId}/`).then(resp => {
      if (!uniformAlive) return;
      const emp = resp.data;
      const u: any = {};
      if (emp?.clothing_size) u.clothing_size = emp.clothing_size;
      if (emp?.clothing_cut) u.clothing_cut = emp.clothing_cut;
      if (emp?.has_received_uniform != null) u.has_received_uniform = emp.has_received_uniform;
      // Merge into ref (don't replace — preserves values if fetch returns empty)
      uniformRef.current = { ...uniformRef.current, ...u };
      initialProfileRef.current = { ...initialProfileRef.current, ...u };
      setProfile((prev: any) => ({ ...prev, ...u }));
    }).catch(err => {
      console.warn('[EmployeeProfileTab] Uniform fetch FAILED:', err);
    });

    return () => { uniformAlive = false; };
  }, [initialProfile]);

  useEffect(() => {
    let alive = true;
    const loadWorkAreas = async () => {
      try {
        const data = await WorkAreaService.fetchWorkAreas();
        if (Array.isArray(data) && data.length > 0 && alive) {
          const byId = new Map<number, { id: number; name: string }>();
          for (const wa of data) {
            byId.set(wa.id, { id: wa.id, name: wa.name });
          }
          const sorted = Array.from(byId.values()).sort((a, b) =>
            a.name.localeCompare(b.name, 'de', { sensitivity: 'base' })
          );
          setWorkAreaOptions(sorted);
        }
      } catch (error) {
        console.error('Failed to load work areas:', error);
      }
    };

    if (workAreaOptions.length === 0) {
      loadWorkAreas();
    }
    return () => { alive = false; };
  }, [workAreaOptions.length]);

  const validateProfile = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (profile?.job?.primary_work_area && !profile?.job?.work_areas?.includes(profile?.job?.primary_work_area)) {
      newErrors.primary_work_area = 'Primary work area must be one of the selected work areas';
    }

    if (profile?.working_pattern?.days_distribution) {
      if (profile.working_pattern.days_distribution.length !== 7) {
        newErrors.days_distribution = 'Days distribution must have 7 values (one per day)';
      }
      if (profile.working_pattern.days_distribution.some((val: number) => val < 0)) {
        newErrors.days_distribution = 'Days distribution values must be >= 0';
      }

      const totalDistributionHours = profile.working_pattern.days_distribution.reduce((sum: number, val: number) => sum + val, 0);
      const expectedHours = profile?.working_pattern?.hours_per_week || 0;
      if (expectedHours > 0) {
        const deviation = Math.abs(totalDistributionHours - expectedHours) / expectedHours;
        if (deviation > 0.2) {
          newErrors.days_distribution_warning = `Warning: Days distribution (${totalDistributionHours}h) deviates more than 20% from hours per week (${expectedHours}h)`;
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).filter((key) => !key.endsWith('_warning')).length === 0;
  };

  const isEmpty = (obj: any) => !obj || Object.keys(obj).length === 0;

  const toISO = (d: string | null | undefined): string => {
    if (!d) return '';
    if (/^\d{4}-\d{2}-\d{2}$/.test(d)) return d;
    const m = /^(\d{2})\.(\d{2})\.(\d{4})$/.exec(d);
    return m ? `${m[3]}-${m[2]}-${m[1]}` : '';
  };

  const toMonthValue = (d: string | null | undefined): string => {
    if (!d) return '';
    if (/^\d{4}-\d{2}$/.test(d)) return d;
    if (/^\d{4}-\d{2}-\d{2}$/.test(d)) return d.slice(0, 7);
    return '';
  };

  const handleSave = async () => {
    if (!profile || !validateProfile()) {
      return;
    }

    if (!initialProfileRef.current) {
      toast.error('Cannot save: initial profile data not loaded');
      return;
    }

    setSaving(true);
    setErrors({});

    try {
      const patch = prepareProfilePatch(initialProfileRef.current, profile);

      if (patch.job?.employment_type) {
      }

      const hasRootFields = ['clothing_size', 'clothing_cut', 'has_received_uniform'].some(k => k in patch);
      const hasQualifications = 'qualifications' in patch;
      if (
        isEmpty(patch) ||
        (!hasRootFields && !hasQualifications && isEmpty(patch.person) && isEmpty(patch.contact) && isEmpty(patch.job) && isEmpty(patch.working_pattern) && isEmpty(patch.payroll) && isEmpty(patch.leave))
      ) {
        setSaving(false);
        toast.info('Keine Änderungen zum Speichern');
        return;
      }

      const optimistic = {
        ...initialProfileRef.current,
        person: { ...(initialProfileRef.current?.person || {}), ...(patch.person || {}) },
        contact: { ...(initialProfileRef.current?.contact || {}), ...(patch.contact || {}) },
        job: { ...(initialProfileRef.current?.job || {}), ...(patch.job || {}) },
        working_pattern: { ...(initialProfileRef.current?.working_pattern || {}), ...(patch.working_pattern || {}) },
        payroll: { ...(initialProfileRef.current?.payroll || {}), ...(patch.payroll || {}) },
        leave: { ...(initialProfileRef.current?.leave || {}), ...(patch.leave || {}) },
        clothing_size: patch.clothing_size ?? initialProfileRef.current?.clothing_size,
        clothing_cut: patch.clothing_cut ?? initialProfileRef.current?.clothing_cut,
        has_received_uniform: patch.has_received_uniform ?? initialProfileRef.current?.has_received_uniform,
      };
      if (onProfileChanged) {
        onProfileChanged(optimistic);
      }

      // Split: uniform fields go via admin employee endpoint, rest via profile endpoint
      const { clothing_size, clothing_cut, has_received_uniform, ...profilePatch } = patch;
      // Persist in ref so refetch/useEffect can restore them
      if (clothing_size !== undefined) uniformRef.current.clothing_size = clothing_size || undefined;
      if (clothing_cut !== undefined) uniformRef.current.clothing_cut = clothing_cut || undefined;
      if (has_received_uniform !== undefined) uniformRef.current.has_received_uniform = has_received_uniform;
      const uniformPatch: Record<string, any> = {};
      if (clothing_size !== undefined) uniformPatch.clothing_size = clothing_size;
      if (clothing_cut !== undefined) uniformPatch.clothing_cut = clothing_cut;
      if (has_received_uniform !== undefined) uniformPatch.has_received_uniform = has_received_uniform;

      const [serverProfile] = await Promise.all([
        Object.keys(profilePatch).length > 0
          ? EmployeeService.updateEmployeeProfile(employeeId, profilePatch as any)
          : Promise.resolve(initialProfileRef.current),
        Object.keys(uniformPatch).length > 0
          ? EmployeeService.updateEmployee(employeeId, uniformPatch as any)
          : Promise.resolve(null),
      ]);

      // Save pending compensation change if any
      if (pendingCompRef.current) {
        const comp = pendingCompRef.current;
        const amountStr = comp.amount.replace(',', '.');
        try {
          let payload: any;
          if (comp.type === 'HOURLY') {
            payload = { compensation_type: 'HOURLY', hourly_rate: amountStr, currency: 'EUR', valid_from: comp.valid_from };
          } else {
            payload = { compensation_type: 'SALARY', salary_monthly_cents: Math.round(parseFloat(amountStr) * 100), currency: 'EUR', valid_from: comp.valid_from };
          }
          try {
            await createCompensationPeriod(employeeId, payload);
          } catch (ce: any) {
            if (ce?.response?.status === 409) {
              await replaceCompensationPeriod(employeeId, payload);
            }
          }
          pendingCompRef.current = null;
        } catch {
          toast.warn('Profil gespeichert, aber Vergütung konnte nicht gespeichert werden');
        }
      }

      // Merge: keep local fields the BE might not return in responses
      const merged = {
        ...serverProfile,
        clothing_size: serverProfile?.clothing_size ?? profile?.clothing_size,
        clothing_cut: serverProfile?.clothing_cut ?? profile?.clothing_cut,
        has_received_uniform: serverProfile?.has_received_uniform ?? profile?.has_received_uniform,
        working_pattern: {
          ...(serverProfile?.working_pattern || {}),
          regular_workdays: serverProfile?.working_pattern?.regular_workdays
            ?? profile?.working_pattern?.regular_workdays
            ?? initialProfileRef.current?.working_pattern?.regular_workdays,
        },
      };
      initialProfileRef.current = JSON.parse(JSON.stringify(merged));
      setProfile(merged);
      if (onProfileChanged) {
        onProfileChanged(merged);
      }

      // Refetch profile to get updated data
      await refetchProfile();

      // Also refetch overview to update leave balance display
      if (refetchOverview) {
        await refetchOverview();
      }

      toast.success('Profil gespeichert');
      setErrors({});
    } catch (err: any) {
      // HttpError wraps AxiosError in err.originalError
      const axiosError = err?.originalError || err;
      const response = axiosError?.response;
      const status = response?.status || err?.status;
      const data = response?.data || err?.data;
      const traceId = response?.headers?.['x-trace-id'] || data?.trace_id || 'N/A';
      const details = data?.details;
      const message = data?.message || data?.detail || 'Validation error';

      console.error('[Profile] Save failed', { status, traceId, data, details, err });

      toast.error(
        <div>
          <strong>Failed to update profile</strong>
          <div style={{ fontSize: 12, marginTop: 4 }}>{message}</div>
          {details && (
            <pre style={{
              fontSize: 11,
              marginTop: 6,
              maxHeight: 200,
              overflow: 'auto',
              background: 'rgba(0, 0, 0, 0.03)',
              padding: 8,
              borderRadius: 4,
              whiteSpace: 'pre-wrap',
            }}>
              {JSON.stringify(details, null, 2)}
            </pre>
          )}
          <div style={{ fontSize: 12, marginTop: 4, color: '#999' }}>Trace ID: {traceId}</div>
        </div>,
        { autoClose: 10000 }
      );
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    await refetchProfile();
    setErrors({});
  };

  if (!profile) {
    return (
      <div style={{ padding: 24, textAlign: 'center' }}>
        <p>Loading profile...</p>
      </div>
    );
  }

  const fieldWrapper: React.CSSProperties = ps.fieldWrapper;

  const inputStyle: React.CSSProperties = ps.inputStyle;

  const labelStyle: React.CSSProperties = ps.labelStyle;

  const sectionHeaderStyle: React.CSSProperties = {
    fontSize: 15,
    fontWeight: 600,
    color: colors.navy,
    margin: '0 0 16px 0',
    paddingBottom: 10,
    borderBottom: `1px solid ${colors.border}`,
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h2 style={{ fontSize: 20, fontWeight: 600, color: colors.navy, margin: 0 }}>
          Employee Profile
        </h2>
        <div style={{ display: 'flex', gap: 12 }}>
          <button
            type="button"
            className="hover-btn-secondary"
            onClick={handleReset}
            disabled={saving}
            style={{
              ...ps.buttonSecondary,
              cursor: saving ? 'not-allowed' : 'pointer',
            }}
          >
            <FaTimes size={12} />
            Reset
          </button>
          <button
            type="button"
            className="hover-btn-primary"
            onClick={handleSave}
            disabled={saving}
            style={{
              ...ps.buttonPrimary,
              background: saving ? colors.border : ps.buttonPrimary.background,
              cursor: saving ? 'not-allowed' : 'pointer',
              boxShadow: saving ? 'none' : ps.buttonPrimary.boxShadow,
            }}
            data-testid="profile-save-button"
          >
            <FaSave size={12} />
            {saving ? 'Speichere…' : 'Save Changes'}
          </button>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 32 }}>
        {/* ─── LEFT COLUMN ─── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

          {/* Personal Data — frequently changed */}
          <section>
            <h3 style={sectionHeaderStyle}>Persönliche Daten</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div className="pa-field" style={fieldWrapper}>
                  <label style={labelStyle}>Vorname</label>
                  <input
                    type="text"
                    style={inputStyle}
                    value={profile?.person?.first_name || ''}
                    onChange={(e) => setProfile({
                      ...profile,
                      person: { ...profile.person, first_name: e.target.value }
                    })}
                    data-testid="profile-first-name-input"
                  />
                </div>
                <div className="pa-field" style={fieldWrapper}>
                  <label style={labelStyle}>Nachname</label>
                  <input
                    type="text"
                    style={inputStyle}
                    value={profile?.person?.last_name || ''}
                    onChange={(e) => setProfile({
                      ...profile,
                      person: { ...profile.person, last_name: e.target.value }
                    })}
                    data-testid="profile-last-name-input"
                  />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div ref={emailRef} className="pa-field" style={fieldWrapper}>
                  <label style={labelStyle}>Email</label>
                  <input
                    type="email"
                    style={inputStyle}
                    value={profile?.person?.email || ''}
                    onChange={(e) => setProfile({
                      ...profile,
                      person: { ...profile.person, email: e.target.value }
                    })}
                    data-testid="profile-email-input"
                  />
                </div>
                <div ref={phoneRef} className="pa-field" style={fieldWrapper}>
                  <label style={labelStyle}>Telefon</label>
                  <input
                    type="tel"
                    style={inputStyle}
                    value={profile?.person?.phone_number || ''}
                    onChange={(e) => setProfile({
                      ...profile,
                      person: { ...profile.person, phone_number: e.target.value }
                    })}
                  />
                </div>
              </div>
            </div>
          </section>

          {/* Address — frequently changed */}
          <section>
            <h3 style={sectionHeaderStyle}>Adresse</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 12 }}>
                <div className="pa-field" style={fieldWrapper}>
                  <label style={labelStyle}>Straße</label>
                  <input
                    type="text"
                    style={inputStyle}
                    value={profile?.contact?.street || ''}
                    onChange={(e) => setProfile({
                      ...profile,
                      contact: { ...profile.contact, street: e.target.value }
                    })}
                  />
                </div>
                <div className="pa-field" style={fieldWrapper}>
                  <label style={labelStyle}>Hausnr.</label>
                  <input
                    type="text"
                    style={inputStyle}
                    value={profile?.contact?.house_no || ''}
                    onChange={(e) => setProfile({
                      ...profile,
                      contact: { ...profile.contact, house_no: e.target.value }
                    })}
                  />
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 12 }}>
                <div className="pa-field" style={fieldWrapper}>
                  <label style={labelStyle}>PLZ</label>
                  <input
                    type="text"
                    style={inputStyle}
                    value={profile?.contact?.zip || ''}
                    onChange={(e) => setProfile({
                      ...profile,
                      contact: { ...profile.contact, zip: e.target.value }
                    })}
                  />
                </div>
                <div className="pa-field" style={fieldWrapper}>
                  <label style={labelStyle}>Stadt</label>
                  <input
                    type="text"
                    style={inputStyle}
                    value={profile?.contact?.city || ''}
                    onChange={(e) => setProfile({
                      ...profile,
                      contact: { ...profile.contact, city: e.target.value }
                    })}
                  />
                </div>
              </div>
            </div>
          </section>

          {/* Payment Instruction — frequently changed */}
          <section>
            <PaymentInstructionSection
              employeeId={employeeId}
              employeeName={`${profile?.person?.first_name || ''} ${profile?.person?.last_name || ''}`.trim() || 'Employee'}
            />
          </section>

          {/* Personal Details — rarely changed */}
          <section>
            <h3 style={sectionHeaderStyle}>Weitere Angaben</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div ref={birthDateRef} className="pa-field" style={fieldWrapper}>
                  <label style={labelStyle}>Geburtsdatum</label>
                  <input
                    type="date"
                    style={inputStyle}
                    value={toISO(profile?.person?.birth_date)}
                    onChange={(e) => setProfile({
                      ...profile,
                      person: { ...profile.person, birth_date: e.target.value }
                    })}
                  />
                </div>
                <div className="pa-field" style={fieldWrapper}>
                  <label style={labelStyle}>Geschlecht</label>
                  <select
                    style={inputStyle}
                    value={profile?.person?.gender || ''}
                    onChange={(e) => setProfile({
                      ...profile,
                      person: { ...profile.person, gender: e.target.value }
                    })}
                  >
                    <option value="">—</option>
                    {GENDER_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div className="pa-field" style={fieldWrapper}>
                  <label style={labelStyle}>Geburtsort</label>
                  <input
                    type="text"
                    style={inputStyle}
                    value={profile?.person?.birth_place || ''}
                    onChange={(e) => setProfile({
                      ...profile,
                      person: { ...profile.person, birth_place: e.target.value }
                    })}
                  />
                </div>
                <div className="pa-field" style={fieldWrapper}>
                  <label style={labelStyle}>Nationalität</label>
                  <input
                    type="text"
                    style={inputStyle}
                    value={profile?.person?.nationality || ''}
                    onChange={(e) => setProfile({
                      ...profile,
                      person: { ...profile.person, nationality: e.target.value }
                    })}
                  />
                </div>
              </div>
            </div>
          </section>

          {/* Uniform / Dienstkleidung — set via App */}
          <section>
            <h3 style={sectionHeaderStyle}>Dienstkleidung</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
              <div className="pa-field" style={fieldWrapper}>
                <label style={labelStyle}>Kleidergröße</label>
                <select
                  style={inputStyle}
                  value={profile?.clothing_size || ''}
                  onChange={(e) => { uniformRef.current.clothing_size = e.target.value || undefined; setProfile({ ...profile, clothing_size: e.target.value }); }}
                >
                  <option value="">—</option>
                  {['XS', 'S', 'M', 'L', 'XL', 'XXL'].map(s => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>
              <div className="pa-field" style={fieldWrapper}>
                <label style={labelStyle}>Schnitt</label>
                <select
                  style={inputStyle}
                  value={profile?.clothing_cut || ''}
                  onChange={(e) => { uniformRef.current.clothing_cut = e.target.value || undefined; setProfile({ ...profile, clothing_cut: e.target.value }); }}
                >
                  <option value="">—</option>
                  <option value="UNISEX">Unisex</option>
                  <option value="FEMALE">Damen</option>
                </select>
              </div>
              <div className="pa-field" style={fieldWrapper}>
                <label style={labelStyle}>Erhalten</label>
                <select
                  style={inputStyle}
                  value={profile?.has_received_uniform === true ? 'yes' : profile?.has_received_uniform === false ? 'no' : ''}
                  onChange={(e) => { const v = e.target.value === 'yes' ? true : e.target.value === 'no' ? false : null; uniformRef.current.has_received_uniform = v; setProfile({ ...profile, has_received_uniform: v }); }}
                >
                  <option value="">—</option>
                  <option value="yes">Ja</option>
                  <option value="no">Nein</option>
                </select>
              </div>
            </div>
          </section>

          {/* Payroll & Tax — rarely changed */}
          <section>
            <h3 style={sectionHeaderStyle}>Payroll & Steuer</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {/* Personalnummern */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <div className="pa-field" style={fieldWrapper}>
                  <label style={labelStyle}>Interne Personalnr.</label>
                  <input
                    type="text"
                    style={inputStyle}
                    value={profile?.person?.internal_personalnr || ''}
                    onChange={(e) => setProfile({
                      ...profile,
                      person: { ...profile.person, internal_personalnr: e.target.value }
                    })}
                    placeholder="z.B. 1001"
                  />
                </div>
                <div className="pa-field" style={fieldWrapper}>
                  <label style={labelStyle}>DATEV Personalnr.</label>
                  <input
                    type="text"
                    style={inputStyle}
                    value={profile?.person?.datev_personalnr || ''}
                    onChange={(e) => setProfile({
                      ...profile,
                      person: { ...profile.person, datev_personalnr: e.target.value }
                    })}
                    placeholder="z.B. 10001"
                  />
                </div>
              </div>
              {/* Steuer */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <div className="pa-field" style={fieldWrapper}>
                  <label style={labelStyle}>Steuer-ID</label>
                  <input
                    type="text"
                    style={inputStyle}
                    value={profile?.payroll?.steuer_id || ''}
                    onChange={(e) => setProfile({
                      ...profile,
                      payroll: { ...profile.payroll, steuer_id: e.target.value }
                    })}
                  />
                </div>
                <div className="pa-field" style={fieldWrapper}>
                  <label style={labelStyle}>Sozialversicherungsnr.</label>
                  <input
                    type="text"
                    style={inputStyle}
                    value={profile?.payroll?.sozialversicherungsnummer || ''}
                    onChange={(e) => setProfile({
                      ...profile,
                      payroll: { ...profile.payroll, sozialversicherungsnummer: e.target.value }
                    })}
                  />
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <div className="pa-field" style={fieldWrapper}>
                  <label style={labelStyle}>Lohnsteuerklasse</label>
                  <select
                    style={inputStyle}
                    value={profile?.payroll?.lohnsteuer_klasse || ''}
                    onChange={(e) => setProfile({
                      ...profile,
                      payroll: { ...profile.payroll, lohnsteuer_klasse: e.target.value }
                    })}
                  >
                    <option value="">—</option>
                    {LOHNSTKL_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
                <div className="pa-field" style={fieldWrapper}>
                  <label style={labelStyle}>Religion</label>
                  <select
                    style={inputStyle}
                    value={profile?.payroll?.religion || ''}
                    onChange={(e) => setProfile({
                      ...profile,
                      payroll: { ...profile.payroll, religion: e.target.value }
                    })}
                  >
                    <option value="">—</option>
                    {RELIGION_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Versicherung */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <div className="pa-field" style={fieldWrapper}>
                  <label style={labelStyle}>Krankenversicherung</label>
                  <select
                    style={inputStyle}
                    value={profile?.payroll?.health_insurance_type || ''}
                    onChange={(e) => setProfile({
                      ...profile,
                      payroll: { ...profile.payroll, health_insurance_type: e.target.value }
                    })}
                  >
                    <option value="">—</option>
                    {HEALTH_INS_TYPE_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
                <div className="pa-field" style={fieldWrapper}>
                  <label style={labelStyle}>Krankenkasse</label>
                  <input
                    type="text"
                    style={inputStyle}
                    value={profile?.payroll?.health_insurance_name || ''}
                    onChange={(e) => setProfile({
                      ...profile,
                      payroll: { ...profile.payroll, health_insurance_name: e.target.value }
                    })}
                  />
                </div>
              </div>
              <div>
                <label style={{ ...labelStyle, display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={profile?.payroll?.is_family_insured || false}
                    onChange={(e) => setProfile({
                      ...profile,
                      payroll: { ...profile.payroll, is_family_insured: e.target.checked }
                    })}
                    style={{ width: 'auto', cursor: 'pointer' }}
                  />
                  Familienversicherung
                </label>
              </div>
            </div>
          </section>

          {/* Payroll Opening Balance */}
          <section>
            <h3 style={sectionHeaderStyle}>Payroll Opening Balance</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div className="pa-field" style={fieldWrapper}>
                  <label style={labelStyle}>Opening Balance (h)</label>
                  <input
                    type="number"
                    style={inputStyle}
                    value={profile?.payroll?.opening_balance_h || ''}
                    onChange={(e) => setProfile({
                      ...profile,
                      payroll: { ...profile.payroll, opening_balance_h: e.target.value ? parseFloat(e.target.value) : null }
                    })}
                    step="0.5"
                    placeholder="z.B. 40.0"
                  />
                </div>
                <div className="pa-field" style={fieldWrapper}>
                  <label style={labelStyle}>Effective Month</label>
                  <input
                    type="month"
                    style={inputStyle}
                    value={toMonthValue(profile?.payroll?.opening_balance_effective_month)}
                    onChange={(e) => setProfile({
                      ...profile,
                      payroll: { ...profile.payroll, opening_balance_effective_month: e.target.value }
                    })}
                    placeholder="YYYY-MM"
                  />
                </div>
              </div>

              <div className="pa-field" style={fieldWrapper}>
                <label style={labelStyle}>Notiz</label>
                <textarea
                  style={{
                    ...inputStyle,
                    minHeight: 80,
                    resize: 'vertical',
                    fontFamily: 'inherit',
                  }}
                  value={profile?.payroll?.opening_balance_note || ''}
                  onChange={(e) => setProfile({
                    ...profile,
                    payroll: { ...profile.payroll, opening_balance_note: e.target.value }
                  })}
                  placeholder="Optionale Notiz zum Opening Balance"
                />
              </div>
            </div>
          </section>

          <section>
            <h3 style={sectionHeaderStyle}>Qualifikationen</h3>
            <QualificationPills
              selectedIds={(profile?.qualifications ?? []).map((q: any) => q.id)}
              onChange={(ids) => setProfile({
                ...profile,
                qualifications: ids.map((id: number) => {
                  const existing = (profile?.qualifications ?? []).find((q: any) => q.id === id);
                  return existing || { id };
                }),
              })}
            />
          </section>
        </div>

        {/* ─── RIGHT COLUMN ─── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 32 }}>

          {/* Contract History Timeline */}
          <section>
            <ContractHistoryTimeline
              profile={profile}
              employeeId={employeeId}
              refetchProfile={refetchProfile}
              onLeaveChange={(leave) => setProfile({ ...profile, leave })}
              onProfileChange={(updater) => setProfile((prev: any) => updater(prev))}
              onPendingCompChange={(comp) => { pendingCompRef.current = comp; }}
              workAreas={workAreaOptions}
              highlightField={highlightField}
              onHighlightDone={onHighlightDone}
            />
          </section>

        </div>
      </div>
    </div>
  );
};

export default EmployeeProfileTab;
